import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import {
  kotaRaporu,
  planDogrula,
  planIzgarasi,
  planOnayla,
  planUret,
  planUretimleri,
  type PlanIhlali,
  type PlanIzgaraSatiri,
  type PlanKotaSatiri,
  type PlanUretimOzeti,
} from '../api/vardiya';
import type { Kimlik } from '../api/tipler';
import { BirimSecici } from '../bilesenler/BirimSecici';
import { Dugme, HataKutusu } from '../bilesenler/temel';
import { bugun, gunAdi, gunAraligi, gunAy, haftaBasi, isoGun, tarihMetni } from '../bicim/tarih';
import { yetkiKullan } from '../yetki/yetki';

/**
 * VARDİYA PLANI.
 *
 * Demo hattının "otomasyon" adımı: şef bir birim ve dönem seçer, dört
 * haftalık plan tek tıkla üretilir, doğrulama sonucu ve kota raporu eşit
 * dağılımı kanıtlar, müdür onaylar.
 *
 * PLAN UFKU 4 HAFTA ve sunucuda sabit (`PLAN_UFKU_HAFTA`). Dönem başlangıcı
 * PAZARTESİYE demirleniyor: rastgele bir güne demirlenirse dört haftalık ufuk
 * hafta ortasında kesilir ve rotasyon deseni ızgarada okunamaz hâle gelir.
 */
const UFUK_HAFTA = 4;

export function VardiyaPlani() {
  const [birimId, birimIdYaz] = useState<Kimlik | undefined>(undefined);
  const [donemBaslangic, donemBaslangicYaz] = useState(() => haftaBasi(bugun()));
  const [acikKota, acikKotaYaz] = useState<Kimlik | null>(null);

  const kuyruk = useQueryClient();

  const uretebilir = yetkiKullan('VARDIYA_PLAN_URET');
  const onaylayabilir = yetkiKullan('VARDIYA_PLAN_ONAYLA');
  /*
   * DOĞRULAMA UCU ÜÇ KODDAN BİRİNİ İSTİYOR.
   *
   * Yalnız `uretebilir` ile kapatılsaydı, sadece ONAYLA yetkisi olan bir
   * müdür onaylayacağı planın ihlallerini göremezdi — onay adımı kör
   * kalırdı.
   */
  const dogrulamaGorebilir = yetkiKullan(
    'VARDIYA_PLAN_URET',
    'VARDIYA_PLAN_DUZELT',
    'VARDIYA_PLAN_ONAYLA',
  );
  /*
   * KOTA UCU ÜÇ KOD İSTİYOR VE LİSTESİ EKRANINKİNDEN DAR.
   *
   * Ekrana `PERSONEL_GORUNTULE` veya `VARDIYA_PLAN_DUZELT` ile de
   * girilebiliyor; ikisi de `/vardiya/plan/:id/kota` listesinde YOK. Düğme
   * koşulsuz gösterilseydi bu kullanıcılar tıklar ve 403 alırdı — sunucuda
   * gereksiz bir KVKK denetim kaydı, ekranda anlaşılmaz bir hata.
   */
  const kotaGorebilir = yetkiKullan('RAPOR_GORUNTULE', 'VARDIYA_PLAN_ONAYLA', 'VARDIYA_PLAN_URET');

  const bitis = useMemo(() => {
    const son = new Date(`${donemBaslangic}T00:00:00`);
    son.setDate(son.getDate() + UFUK_HAFTA * 7 - 1);
    const yil = son.getFullYear();
    const ay = String(son.getMonth() + 1).padStart(2, '0');
    const gun = String(son.getDate()).padStart(2, '0');
    return `${yil}-${ay}-${gun}`;
  }, [donemBaslangic]);

  const sorgu = birimId === undefined ? null : { birimId, baslangic: donemBaslangic, bitis };

  const izgara = useQuery({
    queryKey: ['vardiya', 'izgara', sorgu],
    queryFn: () => planIzgarasi(sorgu!),
    // Birim seçilmeden istek gitmez; sunucu `birimId` zorunlu ve 400 verirdi.
    enabled: sorgu !== null,
  });

  const uretimler = useQuery({
    queryKey: ['vardiya', 'uretim', sorgu],
    queryFn: () => planUretimleri(sorgu!),
    enabled: sorgu !== null,
  });

  const dogrulama = useQuery({
    queryKey: ['vardiya', 'dogrula', sorgu],
    queryFn: () => planDogrula(sorgu!),
    // Doğrulama YALNIZ plan varsa anlamlı; boş dönemde 4857/46 ihlali
    // "hafta tatili yok" diye görünür ve şefi yanıltır.
    enabled: sorgu !== null && (izgara.data?.length ?? 0) > 0 && dogrulamaGorebilir,
  });

  const kota = useQuery({
    queryKey: ['vardiya', 'kota', acikKota],
    queryFn: () => kotaRaporu(acikKota!),
    enabled: acikKota !== null && kotaGorebilir,
  });

  const tazele = () => {
    void kuyruk.invalidateQueries({ queryKey: ['vardiya'] });
  };

  const uret = useMutation({
    mutationFn: () => planUret({ birimId: birimId!, donemBaslangic }),
    onSuccess: (rapor) => {
      // Yetkisi yoksa panel açılmaz; açılsaydı üretimin hemen ardından
      // görünen kutu 403 gösterirdi.
      if (kotaGorebilir) acikKotaYaz(rapor.planUretimiId);
      tazele();
    },
  });

  const onayla = useMutation({
    mutationFn: (girdi: { id: Kimlik; gerekce?: string }) => planOnayla(girdi.id, girdi.gerekce),
    onSuccess: tazele,
  });

  const gunler = useMemo(() => gunAraligi(donemBaslangic, bitis), [donemBaslangic, bitis]);
  const kisiler = useMemo(() => izgaradanKisiler(izgara.data ?? []), [izgara.data]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Vardiya planı</h1>
        <p className="mt-1 text-sm text-slate-600">
          Dört haftalık plan otomatik üretilir, taslak kalır. Müdür onaylamadan personele
          gönderilmez.
        </p>
      </div>

      <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3">
        {/*
          KADEME 3–5: plan EKİP düzeyinde üretiliyor (rotasyon grupları ekibe
          bağlı). Personel listesindeki K3–K4 süzgeci burada yetmez.
        */}
        <BirimSecici deger={birimId} degisti={birimIdYaz} enAltKademe={5} kendiBirimiyleBasla />

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Dönem başlangıcı</span>
          <input
            type="date"
            value={donemBaslangic}
            onChange={(olay) => {
              // Pazartesiye demirlenir; sunucu ufku buradan sayıyor.
              if (olay.target.value) donemBaslangicYaz(haftaBasi(olay.target.value));
            }}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
          />
          <span className="mt-1 block text-xs text-slate-500">
            {tarihMetni(donemBaslangic)} – {tarihMetni(bitis)} · {UFUK_HAFTA} hafta
          </span>
        </label>

        <div className="flex items-end">
          {uretebilir && (
            <Dugme
              tikla={() => uret.mutate()}
              pasif={birimId === undefined}
              bekliyor={uret.isPending}
              cocuk="Planı üret"
            />
          )}
        </div>
      </div>

      <HataKutusu hata={uret.error ?? onayla.error ?? izgara.error ?? uretimler.error} />

      {birimId === undefined && (
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Başlamak için bir birim seçin.
        </p>
      )}

      {sorgu !== null && (
        <>
          <UretimListesi
            uretimler={uretimler.data ?? []}
            bekliyor={uretimler.isPending}
            onaylayabilir={onaylayabilir}
            onayla={(id, gerekce) => onayla.mutate({ id, gerekce })}
            onayBekliyor={onayla.isPending}
            kotaAc={acikKotaYaz}
            acikKota={acikKota}
            kotaGorebilir={kotaGorebilir}
          />

          {dogrulama.data && dogrulama.data.length > 0 && <Dogrulama ihlaller={dogrulama.data} />}

          {acikKota !== null && kotaGorebilir && (
            <KotaTablosu
              satirlar={kota.data ?? []}
              bekliyor={kota.isPending}
              hata={kota.error}
              kapat={() => acikKotaYaz(null)}
            />
          )}

          <Izgara
            gunler={gunler}
            kisiler={kisiler}
            bekliyor={izgara.isPending}
            bos={(izgara.data?.length ?? 0) === 0}
          />
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Izgara                                                              */
/* ------------------------------------------------------------------ */

interface Kisi {
  atamaId: Kimlik;
  personelNo: string;
  adSoyad: string;
  /** `YYYY-MM-DD` → o günün plan satırı. */
  gunler: Map<string, PlanIzgaraSatiri>;
}

/**
 * Düz satır listesini kişi × gün tablosuna çevirir.
 *
 * Sunucu satırları kişi ve tarihe göre sıralı gönderiyor ama ızgara SEYREK
 * olabilir: dönem içinde işe giren personelin ilk günleri yoktur. Bu yüzden
 * gün sütunları listeden değil, dönem aralığından üretiliyor; kişide o gün
 * yoksa hücre boş kalır.
 */
function izgaradanKisiler(satirlar: PlanIzgaraSatiri[]): Kisi[] {
  const harita = new Map<string, Kisi>();

  for (const satir of satirlar) {
    let kisi = harita.get(satir.atamaId);
    if (!kisi) {
      kisi = {
        atamaId: satir.atamaId,
        personelNo: satir.personelNo,
        adSoyad: satir.adSoyad,
        gunler: new Map(),
      };
      harita.set(satir.atamaId, kisi);
    }
    kisi.gunler.set(isoGun(satir.tarih), satir);
  }

  return [...harita.values()];
}

function Izgara({
  gunler,
  kisiler,
  bekliyor,
  bos,
}: {
  gunler: string[];
  kisiler: Kisi[];
  bekliyor: boolean;
  bos: boolean;
}) {
  if (bekliyor) {
    return <p className="text-sm text-slate-500">Plan yükleniyor…</p>;
  }

  if (bos) {
    return (
      <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
        Bu dönemde plan yok. Yukarıdan üretebilirsiniz.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="text-xs">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            {/*
              Ad sütunu YAPIŞIK: 28 günlük ızgarada yatay kaydırırken kimin
              satırına bakıldığı kaybolur.
            */}
            <th className="sticky left-0 z-10 min-w-40 bg-slate-50 px-3 py-2 text-left font-medium text-slate-600">
              Personel
            </th>
            {gunler.map((gun) => (
              <th key={gun} className="min-w-11 px-1 py-2 text-center font-medium text-slate-500">
                <span className="block">{gunAy(`${gun}T00:00:00`)}</span>
                <span className="block text-[10px] text-slate-400">{gunAdi(isoGunNo(gun))}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {kisiler.map((kisi) => (
            <tr key={kisi.atamaId}>
              <td className="sticky left-0 z-10 bg-white px-3 py-1.5">
                <span className="block truncate text-slate-900">{kisi.adSoyad}</span>
                <span className="block font-mono text-[10px] text-slate-400">
                  {kisi.personelNo}
                </span>
              </td>
              {gunler.map((gun) => (
                <Hucre key={gun} satir={kisi.gunler.get(gun)} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <Gosterge />
    </div>
  );
}

/** `YYYY-MM-DD` → ISO gün numarası (1 Pazartesi … 7 Pazar). */
function isoGunNo(gun: string): number {
  const haftaGunu = new Date(`${gun}T00:00:00`).getDay();
  return haftaGunu === 0 ? 7 : haftaGunu;
}

function Hucre({ satir }: { satir: PlanIzgaraSatiri | undefined }) {
  if (!satir) {
    return <td className="px-1 py-1.5 text-center text-slate-300">·</td>;
  }

  const bicim = hucreBicimi(satir);

  return (
    <td className="px-1 py-1.5 text-center">
      <span
        className={`inline-block w-full rounded px-1 py-0.5 text-[11px] ${bicim.sinif}`}
        title={hucreIpucu(satir)}
      >
        {bicim.metin}
      </span>
    </td>
  );
}

function hucreBicimi(satir: PlanIzgaraSatiri): { metin: string; sinif: string } {
  if (satir.gunTipi === 'izinli') {
    return { metin: 'İ', sinif: 'bg-sky-100 text-sky-800' };
  }

  if (satir.gunTipi === 'kanuni_hafta_tatili') {
    return { metin: 'T', sinif: 'bg-slate-100 text-slate-500' };
  }

  // Onay sonrası değiştirilen satır AYRI renkte: müdürün onayladığı plan ile
  // sahadaki plan arasındaki fark görünmezse onay adımı anlamını yitirir.
  if (satir.onaySonrasiDegisiklik) {
    return { metin: satir.vardiyaKod ?? '?', sinif: 'bg-amber-100 text-amber-900 font-medium' };
  }

  if (satir.ihlalKodlari.length > 0) {
    return { metin: satir.vardiyaKod ?? '?', sinif: 'bg-red-100 text-red-800 font-medium' };
  }

  return { metin: satir.vardiyaKod ?? '?', sinif: 'bg-emerald-50 text-emerald-800' };
}

function hucreIpucu(satir: PlanIzgaraSatiri): string {
  const parcalar: string[] = [tarihMetni(satir.tarih)];

  if (satir.vardiyaKod) {
    parcalar.push(
      `${satir.vardiyaKod} ${satir.vardiyaBaslangic ?? ''}–${satir.vardiyaBitis ?? ''}`,
    );
  }
  if (satir.gunTipi === 'kanuni_hafta_tatili') parcalar.push('Kanuni hafta tatili');
  if (satir.gunTipi === 'izinli') parcalar.push('İzinli');
  if (satir.takvimGunTipi) parcalar.push(`Takvim: ${satir.takvimGunTipi}`);
  if (satir.onaySonrasiDegisiklik) parcalar.push('Onay sonrası değiştirildi');
  if (satir.ihlalKodlari.length > 0) parcalar.push(`İhlal: ${satir.ihlalKodlari.join(', ')}`);

  return parcalar.join(' · ');
}

function Gosterge() {
  const ogeler = [
    { sinif: 'bg-emerald-50 text-emerald-800', etiket: 'Çalışma' },
    { sinif: 'bg-slate-100 text-slate-500', etiket: 'Hafta tatili' },
    { sinif: 'bg-sky-100 text-sky-800', etiket: 'İzinli' },
    { sinif: 'bg-amber-100 text-amber-900', etiket: 'Onay sonrası değişti' },
    { sinif: 'bg-red-100 text-red-800', etiket: 'İhlal işaretli' },
  ];

  return (
    <div className="flex flex-wrap gap-3 border-t border-slate-200 px-3 py-2 text-[11px] text-slate-600">
      {ogeler.map((o) => (
        <span key={o.etiket} className="flex items-center gap-1.5">
          <span className={`inline-block h-3 w-5 rounded ${o.sinif}`} />
          {o.etiket}
        </span>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Üretim listesi ve onay                                              */
/* ------------------------------------------------------------------ */

const DURUM_BICIMI: Record<string, string> = {
  taslak: 'bg-amber-100 text-amber-800',
  onayli: 'bg-emerald-100 text-emerald-800',
  iptal: 'bg-slate-200 text-slate-500',
};

function UretimListesi({
  uretimler,
  bekliyor,
  onaylayabilir,
  onayla,
  onayBekliyor,
  kotaAc,
  acikKota,
  kotaGorebilir,
}: {
  uretimler: PlanUretimOzeti[];
  bekliyor: boolean;
  onaylayabilir: boolean;
  onayla: (id: Kimlik, gerekce?: string) => void;
  onayBekliyor: boolean;
  kotaAc: (id: Kimlik | null) => void;
  acikKota: Kimlik | null;
  kotaGorebilir: boolean;
}) {
  /*
   * GEREKÇE ÜRETİM BAŞINA TUTULUR.
   *
   * Tek bir metin kutusu paylaşılsaydı, aynı dönemde birden çok taslak
   * olduğunda birine yazılan gerekçe hepsinde görünür ve yanlış planın
   * onayına iliştirilirdi.
   */
  const [gerekceler, gerekcelerYaz] = useState<Record<string, string>>({});

  if (bekliyor) return <p className="text-sm text-slate-500">Üretimler yükleniyor…</p>;
  if (uretimler.length === 0) return null;

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-slate-900">Plan üretimleri</h2>

      {uretimler.map((u) => {
        // ENGEL GEREKÇEYLE GEÇİLEMEZ, uyarı geçilebilir. Gerekçe kutusu
        // yalnız uyarı varken açılıyor; engelde açılsaydı kullanıcı
        // yazdıktan sonra 400 alırdı.
        const engelVar = u.ihlalSayisi > 0;
        const gerekceGerekli = u.uyariSayisi > 0;
        const gerekce = gerekceler[u.planUretimiId] ?? '';
        // Sunucu uyarı varken gerekçesiz onayı 400 ile reddediyor; düğme
        // önden kapatılmazsa kullanıcı sebebini anlamadan hata alır.
        const onaylanabilir = !engelVar && (!gerekceGerekli || gerekce.trim().length > 0);

        return (
          <div
            key={u.planUretimiId}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
          >
            <span className={`rounded px-1.5 py-0.5 text-xs ${DURUM_BICIMI[u.durum] ?? ''}`}>
              {u.durum}
            </span>

            <span className="text-slate-700">
              {tarihMetni(u.donemBaslangic)} – {tarihMetni(u.donemBitis)}
            </span>

            <span className="text-xs text-slate-500">
              {u.rotasyonDesenKod} · {u.uretilenSatir} satır
              {u.uretenAdSoyad ? ` · ${u.uretenAdSoyad}` : ''}
            </span>

            {engelVar && (
              <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-800">
                {u.ihlalSayisi} engel
              </span>
            )}
            {u.uyariSayisi > 0 && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                {u.uyariSayisi} uyarı
              </span>
            )}
            {u.onaySonrasiDegisiklikSayisi > 0 && (
              <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-700">
                onay sonrası {u.onaySonrasiDegisiklikSayisi} değişiklik
              </span>
            )}

            <div className="ml-auto flex items-center gap-2">
              {kotaGorebilir && (
                <button
                  type="button"
                  onClick={() => kotaAc(acikKota === u.planUretimiId ? null : u.planUretimiId)}
                  className="rounded px-2 py-1 text-xs text-kurum-700 hover:bg-kurum-50"
                >
                  {acikKota === u.planUretimiId ? 'Kotayı gizle' : 'Kota raporu'}
                </button>
              )}

              {onaylayabilir && u.durum === 'taslak' && (
                <Dugme
                  tur="ikincil"
                  pasif={!onaylanabilir || onayBekliyor}
                  tikla={() => onayla(u.planUretimiId, gerekceGerekli ? gerekce.trim() : undefined)}
                  cocuk={engelVar ? 'Engel var' : 'Onayla'}
                />
              )}
            </div>

            {onaylayabilir && u.durum === 'taslak' && gerekceGerekli && !engelVar && (
              <input
                value={gerekce}
                onChange={(olay) =>
                  gerekcelerYaz((onceki) => ({
                    ...onceki,
                    [u.planUretimiId]: olay.target.value,
                  }))
                }
                placeholder="Uyarı gerekçesi — onay için zorunlu"
                className="w-full rounded-md border border-amber-300 px-3 py-1.5 text-xs outline-none focus:border-amber-500"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Doğrulama ve kota                                                   */
/* ------------------------------------------------------------------ */

function Dogrulama({ ihlaller }: { ihlaller: PlanIhlali[] }) {
  const engeller = ihlaller.filter((i) => i.seviye === 'engel');
  const uyarilar = ihlaller.filter((i) => i.seviye === 'uyari');

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-slate-900">Doğrulama</h2>

      {/*
        ENGEL VE UYARI AYRI GÖSTERİLİR. Aynı listede olsalardı, geçilemeyen
        bir hafta tatili ihlali (4857/46) "gerekçe yaz ve devam et" sanılırdı.
      */}
      {engeller.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-xs font-medium text-red-800">
            {engeller.length} engel — gerekçeyle geçilemez, plan onaylanamaz
          </p>
          <ul className="mt-1 space-y-0.5">
            {engeller.slice(0, 8).map((i, sira) => (
              <li key={`${i.atamaId}-${i.tarih}-${sira}`} className="text-xs text-red-700">
                {tarihMetni(i.tarih)} · {i.detay}
              </li>
            ))}
          </ul>
          {engeller.length > 8 && (
            <p className="mt-1 text-xs text-red-600">…ve {engeller.length - 8} tane daha</p>
          )}
        </div>
      )}

      {uyarilar.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-900">
            {uyarilar.length} uyarı — gerekçe yazılarak onaylanabilir
          </p>
          <ul className="mt-1 space-y-0.5">
            {uyarilar.slice(0, 8).map((i, sira) => (
              <li key={`${i.atamaId}-${i.tarih}-${sira}`} className="text-xs text-amber-800">
                {i.detay}
              </li>
            ))}
          </ul>
          {uyarilar.length > 8 && (
            <p className="mt-1 text-xs text-amber-700">…ve {uyarilar.length - 8} tane daha</p>
          )}
        </div>
      )}
    </div>
  );
}

function KotaTablosu({
  satirlar,
  bekliyor,
  hata,
  kapat,
}: {
  satirlar: PlanKotaSatiri[];
  bekliyor: boolean;
  hata: unknown;
  kapat: () => void;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Kota raporu</h2>
          <p className="text-xs text-slate-500">
            Beş kota: borç · toplam vardiya · Pazar tatili · V1/V2 · hafta sonu. Yalnız toplam
            eşitlenirse dağılım bozulur.
          </p>
        </div>
        <button
          type="button"
          onClick={kapat}
          className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
        >
          Kapat
        </button>
      </div>

      <HataKutusu hata={hata} />

      {bekliyor ? (
        <p className="p-4 text-sm text-slate-500">Yükleniyor…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Personel</th>
                <th className="px-3 py-2 font-medium">Hedef</th>
                <th className="px-3 py-2 font-medium">Üretilen</th>
                <th className="px-3 py-2 font-medium">Sapma</th>
                <th className="px-3 py-2 font-medium">Pazar</th>
                <th className="px-3 py-2 font-medium">V1 / V2</th>
                <th className="px-3 py-2 font-medium">Hafta sonu</th>
                <th className="px-3 py-2 font-medium">Borç</th>
                <th className="px-3 py-2 font-medium">Hf. ort.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {satirlar.map((s) => (
                <tr key={s.atamaId}>
                  <td className="px-3 py-1.5">
                    <span className="block text-slate-900">{s.adSoyad}</span>
                    <span className="block font-mono text-[10px] text-slate-400">
                      {s.personelNo}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-slate-700">{s.hedefVardiya}</td>
                  <td className="px-3 py-1.5 text-slate-700">{s.uretilenVardiya}</td>
                  {/*
                    Sapma METİN olarak basılıyor. Number'a çevrilseydi
                    "-0.50" → -0.5 olur ve yarım vardiyalık fark gözden
                    kaçardı; denetlenen büyüklük tam olarak bu.
                  */}
                  <td className={`px-3 py-1.5 font-medium ${sapmaBicimi(s.sapma)}`}>{s.sapma}</td>
                  <td className="px-3 py-1.5 text-slate-700">{s.pazarTatiliSayisi}</td>
                  <td className="px-3 py-1.5 text-slate-700">
                    {s.v1Sayisi} / {s.v2Sayisi}
                  </td>
                  <td className="px-3 py-1.5 text-slate-700">{s.haftaSonuCalisma}</td>
                  <td className="px-3 py-1.5 text-slate-700">
                    {s.acikBorcSayisi > 0 ? (
                      <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-800">
                        {s.acikBorcSayisi} açık
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-slate-700">{s.haftalikOrtalamaSaat ?? '—'}</td>
                </tr>
              ))}

              {satirlar.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                    Kota kaydı yok.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Sapma rengi.
 *
 * Metin karşılaştırması YAPILMAZ, sayıya çevrilir — ama YALNIZ renk için.
 * Ekrana basılan değer metnin kendisidir; burada bir yuvarlama olsa bile
 * gösterilen rakam bozulmaz.
 */
function sapmaBicimi(sapma: string): string {
  const sayi = Number(sapma);
  if (!Number.isFinite(sayi) || sayi === 0) return 'text-slate-700';
  return Math.abs(sayi) > 1 ? 'text-red-700' : 'text-amber-700';
}
